import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { ScanResult } from "../api/types";
import { useApp } from "../state/AppContext";
import { Badge, Field, Spinner } from "../components/ui";
import { formatBytes } from "../player/time";

const REASON_LABEL: Record<string, string> = {
  no_pattern_match: "No left/right token",
  missing_partner: "Partner missing",
  duplicate_eye: "Duplicate eye",
  excluded_by_name: "Excluded by name",
};

export function ImportDialog({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const { notify, info } = useApp();
  const [path, setPath] = useState(localStorage.getItem("pads.lastImportPath") ?? "~");
  const [entries, setEntries] = useState<{ name: string; path: string; is_dir: boolean }[]>([]);
  const [parent, setParent] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState("reference");
  const [task, setTask] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState<{ left?: string; right?: string }>({});

  const browse = useCallback(async (target: string) => {
    setError(null);
    try {
      const r = await api.browse(target);
      setPath(r.path);
      setParent(r.parent);
      setEntries(r.entries);
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { void browse(path); /* eslint-disable-next-line */ }, []);

  const runScan = useCallback(async () => {
    setBusy(true);
    setError(null);
    setScan(null);
    try {
      const result = await api.scan(path);
      setScan(result);
      setSelected(new Set(result.pairs.map((p) => p.key)));
      localStorage.setItem("pads.lastImportPath", path);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [path]);

  const doImport = useCallback(async () => {
    if (!scan) return;
    setBusy(true);
    try {
      const r = await api.commit({ path: scan.root, keys: [...selected], import_mode: mode, task: task || undefined });
      notify(`Importing ${r.count} session${r.count === 1 ? "" : "s"} — indexing runs in the background`, "success");
      onImported();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [scan, selected, mode, task, notify, onImported, onClose]);

  const pairManually = useCallback(async () => {
    if (!manual.left || !manual.right) return;
    setBusy(true);
    try {
      await api.manualPair({ left: manual.left, right: manual.right });
      notify("Paired manually — indexing started", "success");
      setManual({});
      onImported();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [manual, notify, onImported, onClose]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="modal-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ height: "min(760px, 100%)" }}>
        <div className="modal-head">
          <h2>Import a folder</h2>
          <span className="spacer" />
          <button className="ghost" onClick={onClose}>Close</button>
        </div>

        <div className="modal-body">
          <div className="row" style={{ marginBottom: 10 }}>
            <input
              value={path}
              onChange={(e) => setPath(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && browse(path)}
              placeholder="/path/to/recordings"
              spellCheck={false}
            />
            <button onClick={() => browse(path)}>Open</button>
            <button className="primary" onClick={runScan} disabled={busy}>
              {busy ? <Spinner /> : null} Scan
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 12, minHeight: 260 }}>
            <div className="panel" style={{ maxHeight: 320 }}>
              <div className="panel-head">Folders</div>
              <div className="panel-body">
                {parent ? (
                  <button className="ghost" style={{ width: "100%", justifyContent: "flex-start" }} onClick={() => browse(parent)}>
                    ../
                  </button>
                ) : null}
                {entries.filter((e) => e.is_dir).map((e) => (
                  <button
                    key={e.path}
                    className="ghost"
                    style={{ width: "100%", justifyContent: "flex-start" }}
                    onClick={() => browse(e.path)}
                    title={e.path}
                  >
                    <span className="truncate">{e.name}/</span>
                  </button>
                ))}
                {entries.length === 0 ? <div className="hint" style={{ padding: 10 }}>Empty folder.</div> : null}
              </div>
            </div>

            <div className="panel">
              <div className="panel-head">
                Detected sessions
                {scan ? <span className="muted" style={{ textTransform: "none", fontWeight: 400 }}>
                  {scan.summary.pairs} pair(s), {scan.summary.unmatched} unmatched, {scan.summary.skipped} skipped
                </span> : null}
              </div>
              <div className="panel-body pad">
                {error ? <div className="error-box" style={{ marginBottom: 10 }}>{error}</div> : null}
                {!scan ? (
                  <div className="hint">
                    Pick a folder and press <kbd>Scan</kbd>. Files are paired by the naming convention the
                    recorder uses — {info?.pairing.patterns.map((p) => p.id).join(", ")} — and anything that
                    cannot be paired is listed below with the reason.
                  </div>
                ) : (
                  <>
                    {scan.pairs.length > 0 ? (
                      <table className="data">
                        <thead>
                          <tr>
                            <th style={{ width: 26 }} />
                            <th>Session</th>
                            <th>Left</th>
                            <th>Right</th>
                            <th style={{ width: 80 }}>Size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scan.pairs.map((p) => (
                            <tr key={p.key} className={selected.has(p.key) ? "selected" : ""}>
                              <td><input type="checkbox" style={{ width: 14 }} checked={selected.has(p.key)} onChange={() => toggle(p.key)} /></td>
                              <td>
                                <div className="mono">{p.key}</div>
                                <div className="tiny muted">{p.pattern_id}</div>
                                {p.warnings.map((w, i) => (
                                  <div key={i} className="tiny" style={{ color: "var(--warn)" }}>{w}</div>
                                ))}
                              </td>
                              <td className="tiny truncate" style={{ maxWidth: 190 }} title={p.left_name}>{p.left_name}</td>
                              <td className="tiny truncate" style={{ maxWidth: 190 }} title={p.right_name}>{p.right_name}</td>
                              <td className="tiny mono">{formatBytes(p.size_bytes)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="hint">No pairs found in this folder.</div>
                    )}

                    {scan.unmatched.length > 0 ? (
                      <div style={{ marginTop: 16 }}>
                        <div className="panel-head" style={{ padding: "6px 0", border: 0 }}>Unmatched — pair these by hand</div>
                        <table className="data">
                          <tbody>
                            {scan.unmatched.map((u) => (
                              <tr key={u.path}>
                                <td className="tiny truncate" style={{ maxWidth: 300 }} title={u.path}>{u.name}</td>
                                <td style={{ width: 150 }}><Badge kind="warning">{REASON_LABEL[u.reason] ?? u.reason}</Badge></td>
                                <td className="tiny muted">{u.detail}</td>
                                <td style={{ width: 130 }}>
                                  <div className="row">
                                    <button className={manual.left === u.path ? "active" : "ghost"} onClick={() => setManual((m) => ({ ...m, left: u.path }))}>L</button>
                                    <button className={manual.right === u.path ? "active" : "ghost"} onClick={() => setManual((m) => ({ ...m, right: u.path }))}>R</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {manual.left || manual.right ? (
                          <div className="row" style={{ marginTop: 8 }}>
                            <span className="tiny muted">
                              L: {manual.left?.split("/").pop() ?? "—"} · R: {manual.right?.split("/").pop() ?? "—"}
                            </span>
                            <button className="primary" disabled={!manual.left || !manual.right || busy} onClick={pairManually}>
                              Pair manually
                            </button>
                            <button className="ghost" onClick={() => setManual({})}>Clear</button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {scan.skipped.length > 0 ? (
                      <details style={{ marginTop: 14 }}>
                        <summary className="tiny muted" style={{ cursor: "pointer" }}>
                          {scan.skipped.length} file(s) skipped as recorder diagnostics
                        </summary>
                        <div className="tiny muted" style={{ marginTop: 6 }}>
                          {scan.skipped.map((s) => <div key={s.path}>{s.name} — {s.detail}</div>)}
                        </div>
                      </details>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <div style={{ width: 190 }}>
            <Field label="Import mode">
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="reference">Reference originals (no copy)</option>
                <option value="managed">Copy into workspace</option>
              </select>
            </Field>
          </div>
          <div style={{ width: 220 }}>
            <Field label="Task (optional)">
              <input value={task} onChange={(e) => setTask(e.target.value)} placeholder="e.g. kitchen pick-and-place" />
            </Field>
          </div>
          <span className="spacer" />
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="primary" onClick={doImport} disabled={!scan || selected.size === 0 || busy}>
            Import {selected.size > 0 ? `${selected.size} session${selected.size === 1 ? "" : "s"}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
